import { Formik } from 'formik'
import { useEffect, useRef } from 'react'
import { toast } from 'react-toastify'

import { Drawer, Icon, Layout } from '@loadsmart/miranda-react'

import { type StopAppointment } from 'core/appointments/domain/Appointment'
import { RequestStatus } from 'core/domain/Request'

import { analytics } from '../../../core/utils/analytics'
import { type Contact } from '../../../shipments/domain/Facility'
import { getAppointmentDate, getInitialValuesFromAppointment } from '../../domain/Appointment'
import { type AppointmentFormValues } from '../../infra/ShipmentRepository'
import { useUpdateAppointment } from '../hooks/useUpdateAppointment'
import { CallFacilityBanner } from './CallFacilityBanner'
import { FacilityContactInformation } from './FacilityContactInformartion'
import { RegularAppointmentDrawerForm } from './RegularAppointmentDrawerForm'
import { getScheduledValidationSchema } from './utils/appointmentSchemas'

const initialValues: AppointmentFormValues = {
  mode: 'appt',
  type: 'requested',
  loadingType: 'live',
  notifyShipper: false,
  status: 'pending',
  misschedulingReason: { label: '', value: '' },
  misschedulingNote: '',
  timeType: { label: 'Specific time', value: 'at' },
  date: '',
  time: '',
  timeEnd: '',
}

export type RegularAppointmentDrawerProps = {
  facilityContact?: Partial<Contact>
  shipmentUUID: string
  stopIndex: number
  stopDate?: string
  appointment?: StopAppointment
  isOpen: boolean
  onClose: () => void
  onConfirm: (values: AppointmentFormValues) => void
}

export function RegularAppointmentDrawer({
  facilityContact,
  shipmentUUID,
  stopIndex,
  stopDate,
  appointment,
  isOpen,
  onClose,
  onConfirm,
}: RegularAppointmentDrawerProps) {
  const { update: updateAppointment, requestStatus } = useUpdateAppointment()

  const formInitialValues = appointment
    ? getInitialValuesFromAppointment(initialValues, appointment)
    : initialValues

  const appointmentDate = getAppointmentDate({ appointment, stopDate })

  const validationSchema = getScheduledValidationSchema(appointment)

  const latestValuesRef = useRef<AppointmentFormValues>(formInitialValues)
  const appointmentRef = useRef(appointment)

  useEffect(() => {
    if (requestStatus === RequestStatus.Done) {
      toast.success('Appointment updated successfully')
      onConfirm(latestValuesRef.current)

      if (!appointmentRef.current) {
        analytics.track('Shipment details / Appointment scheduled', {
          appointmentType: latestValuesRef.current.mode,
          loadingType: latestValuesRef.current.loadingType,
          timeType: latestValuesRef.current.timeType?.value,
          hasId: Boolean(latestValuesRef.current.confirmationNumber),
          is_opendock: false,
        })
      } else {
        analytics.track('Rescheduled: appointment', {
          is_opendock: false,
          reschedule_reason: latestValuesRef.current.misschedulingReason?.value,
        })
      }
    }

    if (requestStatus === RequestStatus.Error) {
      toast.error('Could not update appointment, try again later', { position: 'top-left' })
    }
  }, [requestStatus, onConfirm])

  if (!isOpen) return null

  return (
    <Formik<AppointmentFormValues>
      initialValues={{ ...formInitialValues, date: appointmentDate }}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnMount
      onSubmit={() => undefined}
    >
      {({ values, isValid, isSubmitting }) => {
        latestValuesRef.current = values

        async function handleConfirm() {
          await updateAppointment({ shipmentUUID, stopIndex, data: values })
        }

        return (
          <Drawer
            open={isOpen}
            onClose={(event: CustomEvent<any>) => {
              if (event.target === event.currentTarget) return
              onClose()
            }}
          >
            <Drawer.Header>
              <Layout.Group align="center" gap="spacing-1">
                <Icon name="calendar" size="24px" />
                Schedule
              </Layout.Group>
              <Drawer.Close />
            </Drawer.Header>

            <Drawer.Body>
              <CallFacilityBanner />
              <FacilityContactInformation facilityContact={facilityContact} />
              <RegularAppointmentDrawerForm appointment={appointment} />
            </Drawer.Body>

            <Drawer.Actions>
              <Drawer.ActionPrimary
                onClick={handleConfirm}
                loading={requestStatus === RequestStatus.Pending}
                disabled={!isValid || isSubmitting}
              >
                Confirm
              </Drawer.ActionPrimary>
            </Drawer.Actions>
          </Drawer>
        )
      }}
    </Formik>
  )
}
